import type { StyleSpecification } from "maplibre-gl";
import { addProtocol } from "maplibre-gl";
import { Protocol } from "pmtiles";
import * as React from "react";
import { useFeature } from "store/selectors/hooks";
import { reportError, shouldSkipReporting, toReportableError } from "util/error-tracking";
import {
    abortBasemapManifestLoad,
    isAbortError,
    loadBasemapManifest,
} from "./basemap-manifest";
import {
    BasemapManifest,
    buildSelfHostedMapStyle,
    MAPTILER_STYLE_URL,
} from "./map-style";

let pmtilesProtocolRegistered = false;

const ensurePmtilesProtocol = (): void => {
    if (pmtilesProtocolRegistered) {
        return;
    }
    addProtocol("pmtiles", new Protocol().tile);
    pmtilesProtocolRegistered = true;
};

export type BasemapPolicy = "allowThirdPartyFallback" | "selfHostedOnly";

export type Basemap = "selfHosted" | "mapTiler";

export type ManifestStatus = "notRequested" | "loading" | "loaded" | "failed";

export type MapStyleResult =
    | { status: "ready"; style: string | StyleSpecification }
    | { status: "loading" }
    | { status: "unavailable"; reason: "featureDisabled" | "manifestUnavailable" };

export interface BasemapState {
    policy: BasemapPolicy | "mapTilerOnly";
    requestedBasemap: Basemap;
    manifestStatus: ManifestStatus;
    result: MapStyleResult;
}

export const MAPTILER_ONLY_BASEMAP: BasemapState = {
    policy: "mapTilerOnly",
    requestedBasemap: "mapTiler",
    manifestStatus: "notRequested",
    result: { status: "ready", style: MAPTILER_STYLE_URL },
};

type ManifestState =
    | { status: "notRequested" | "loading" | "failed" }
    | { status: "loaded"; manifest: BasemapManifest };

const reportManifestFailure = (e: unknown): void => {
    if (shouldSkipReporting(e)) {
        return;
    }
    const error = toReportableError(e);
    reportError(error, { fingerprint: `basemap-manifest:${error.name}` });
};

const useBasemapManifest = (): ManifestState => {
    const enabled = useFeature("selfHostedMapTiles");
    const [manifest, setManifest] = React.useState<BasemapManifest | null>(null);
    const [manifestFailed, setManifestFailed] = React.useState(false);
    React.useEffect(() => {
        if (!enabled) {
            return undefined;
        }
        let active = true;
        const load = () => {
            loadBasemapManifest()
                .then(result => {
                    if (active) {
                        setManifest(result);
                    }
                })
                .catch((e: unknown) => {
                    if (isAbortError(e)) {
                        return;
                    }
                    if (active) {
                        setManifestFailed(true);
                    }
                    reportManifestFailure(e);
                });
        };
        const onPageHide = () => abortBasemapManifestLoad();
        const onPageShow = (event: PageTransitionEvent) => {
            if (event.persisted && active) {
                load();
            }
        };
        window.addEventListener("pagehide", onPageHide);
        window.addEventListener("pageshow", onPageShow);
        load();
        return () => {
            active = false;
            window.removeEventListener("pagehide", onPageHide);
            window.removeEventListener("pageshow", onPageShow);
        };
    }, [enabled]);
    return React.useMemo((): ManifestState => {
        if (!enabled) {
            return { status: "notRequested" };
        }
        if (manifest) {
            return { status: "loaded", manifest };
        }
        return { status: manifestFailed ? "failed" : "loading" };
    }, [enabled, manifest, manifestFailed]);
};

const selfHostedOnlyPlaceholder = (
    status: "notRequested" | "loading" | "failed",
): MapStyleResult => {
    switch (status) {
        case "notRequested":
            return { status: "unavailable", reason: "featureDisabled" };
        case "failed":
            return { status: "unavailable", reason: "manifestUnavailable" };
        case "loading":
            return { status: "loading" };
        default: {
            const exhaustiveCheck: never = status;
            throw new Error(`Unhandled manifest status ${exhaustiveCheck}`);
        }
    }
};

const resolveMapStyle = (
    policy: BasemapPolicy,
    manifest: ManifestState,
): MapStyleResult => {
    if (manifest.status === "loaded") {
        ensurePmtilesProtocol();
        return {
            status: "ready",
            style: buildSelfHostedMapStyle(manifest.manifest.tiles),
        };
    }
    switch (policy) {
        case "allowThirdPartyFallback":
            return { status: "ready", style: MAPTILER_STYLE_URL };
        case "selfHostedOnly":
            return selfHostedOnlyPlaceholder(manifest.status);
        default: {
            const exhaustiveCheck: never = policy;
            throw new Error(`Unhandled basemap policy ${exhaustiveCheck}`);
        }
    }
};

const requestedBasemapFor = (policy: BasemapPolicy, manifest: ManifestState): Basemap =>
    manifest.status !== "notRequested" || policy === "selfHostedOnly"
        ? "selfHosted"
        : "mapTiler";

export const useBasemap = (policy: BasemapPolicy): BasemapState => {
    const manifest = useBasemapManifest();
    return React.useMemo(
        (): BasemapState => ({
            policy,
            requestedBasemap: requestedBasemapFor(policy, manifest),
            manifestStatus: manifest.status,
            result: resolveMapStyle(policy, manifest),
        }),
        [policy, manifest],
    );
};
